import { useWallet } from "@solana/wallet-adapter-react";
import { useCallback, useEffect, useState } from "react";
import { 
  isMobileDevice, 
  detectMobileWallet, 
  clearMobileWalletCache 
} from "@/lib/mobileWalletFix";

export function useWalletSafe() {
  const wallet = useWallet();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileWallet, setMobileWallet] = useState<string | null>(null);
  const [connectionAttempts, setConnectionAttempts] = useState(0);

  // Disconnect con cleanup mobile
  const disconnect = useCallback(async () => {
    try {
      await wallet.disconnect();
      if (isMobile) {
        clearMobileWalletCache();
      }
    } catch (error) {
      console.error("Error disconnecting wallet:", error);
    }
  }, [wallet, isMobile]);

  // Connect con retry logic per mobile
  const connect = useCallback(async () => {
    try {
      console.log("[useWalletSafe] Attempting to connect...");
      setConnectionAttempts(prev => prev + 1);
      
      if (!wallet.wallet) {
        console.log("[useWalletSafe] No wallet selected");
        return;
      }
      
      await wallet.connect();
      console.log("[useWalletSafe] Connected successfully");
    } catch (error: any) {
      console.error("[useWalletSafe] Connection error:", error);
      
      // Se su mobile e fallisce, prova a riconnettere dopo un delay
      if (isMobile && connectionAttempts < 3) {
        console.log("[useWalletSafe] Retrying connection in 1s...");
        setTimeout(() => {
          connect();
        }, 1000);
      }
    }
  }, [wallet, isMobile, connectionAttempts]);

  // Detect mobile
  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);
    console.log("[useWalletSafe] Mobile detected:", mobile);
  }, []);

  // Detect mobile wallet con logging
  useEffect(() => {
    const checkMobileWallet = () => {
      const detected = detectMobileWallet();
      console.log("[useWalletSafe] Detected mobile wallet:", detected);
      if (detected) {
        setMobileWallet(detected);
        localStorage.setItem("detectedMobileWallet", detected);
      }
    };

    checkMobileWallet();
    window.addEventListener("load", checkMobileWallet);

    // Re-check quando wallet si connette
    if (wallet.connected) {
      checkMobileWallet();
    }

    return () => {
      window.removeEventListener("load", checkMobileWallet);
    };
  }, [wallet.connected]);

  // Log wallet state changes per debug
  useEffect(() => {
    console.log("[useWalletSafe] Wallet state:", {
      connected: wallet.connected,
      connecting: wallet.connecting,
      publicKey: wallet.publicKey?.toBase58()?.slice(0, 8),
      walletName: wallet.wallet?.adapter?.name,
      hasSignTransaction: !!wallet.signTransaction,
      hasSendTransaction: !!wallet.sendTransaction,
      hasSignMessage: !!wallet.signMessage,
    });
  }, [wallet.connected, wallet.connecting, wallet.publicKey, wallet.wallet]);

  // Fallback per signTransaction su mobile
  const signTransaction = wallet.signTransaction || (async (tx: any) => {
    const adapter = wallet.wallet?.adapter as any;
    if (adapter?.signTransaction) {
      console.log("[useWalletSafe] Using adapter.signTransaction fallback");
      return adapter.signTransaction(tx);
    }
    throw new Error("signTransaction not available - wallet may not support this feature");
  });

  // Fallback per signAllTransactions su mobile
  const signAllTransactions = wallet.signAllTransactions || (async (txs: any) => {
    const adapter = wallet.wallet?.adapter as any;
    if (adapter?.signAllTransactions) {
      console.log("[useWalletSafe] Using adapter.signAllTransactions fallback");
      return adapter.signAllTransactions(txs);
    }
    throw new Error("signAllTransactions not available");
  });

  // Fallback per signMessage su mobile
  const signMessage = wallet.signMessage || (async (message: any) => {
    const adapter = wallet.wallet?.adapter as any;
    if (adapter?.signMessage) {
      console.log("[useWalletSafe] Using adapter.signMessage fallback");
      return adapter.signMessage(message);
    }
    throw new Error("signMessage not available");
  });

  return {
    ...wallet,
    wallet: wallet.wallet,
    disconnect,
    connect,
    signTransaction,
    signAllTransactions,
    signMessage,
    isMobile,
    mobileWallet,
  };
}
